import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import {
  ApiError,
  flowsApi,
  passportsApi,
  type ProductPassportCreate,
  type ProductPassportRead,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export default function Passport() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const item = useQuery({
    queryKey: ["flow", id],
    queryFn: () => flowsApi.get(id!),
    enabled: !!id,
  });
  const passport = useQuery({
    queryKey: ["passport", "by-item", id],
    queryFn: () => passportsApi.byItem(id!),
    enabled: !!id,
    retry: false,
  });

  if (!id) return <ErrorState error={new Error("Bad id")} />;

  return (
    <>
      <PageHeader
        eyebrow={t("passport.eyebrow")}
        title={item.data ? t("passport.titleWithCode", { code: item.data.code }) : t("passport.title")}
        description={
          item.data?.rfid_tag
            ? `RFID: ${item.data.rfid_tag}`
            : t("passport.description")
        }
        actions={<Link to={`/traceability?id=${id}`} className="btn">{t("passport.toTraceability")}</Link>}
      />

      {item.isError && <ErrorState error={item.error} />}
      {passport.isLoading || item.isLoading ? (
        <LoadingState />
      ) : passport.isError && passport.error instanceof ApiError && passport.error.status === 404 ? (
        <IssueForm itemId={id} rfidTag={item.data?.rfid_tag ?? null} />
      ) : passport.isError ? (
        <ErrorState error={passport.error} />
      ) : passport.data ? (
        <PassportView passport={passport.data} />
      ) : null}
    </>
  );
}

function PassportView({ passport }: { passport: ProductPassportRead }) {
  const { t } = useTranslation();
  const reprint = useMutation({
    mutationFn: () => passportsApi.reprint(passport.item_id),
    onSuccess: (r) => alert(t("passport.reprintEnqueued", { job: r.job_id })),
  });
  return (
    <>
      <section className="flex items-center justify-end gap-2">
        <button className="btn btn-primary btn-sm" onClick={() => reprint.mutate()} disabled={reprint.isPending}>
          {reprint.isPending ? t("passport.reprinting") : t("passport.reprint")}
        </button>
      </section>
      {reprint.isError && (
        <ErrorBanner onDismiss={() => reprint.reset()}>{(reprint.error as Error).message}</ErrorBanner>
      )}

      <section className="card p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Field label={t("passport.passportId")} value={<span className="font-mono text-xs">{passport.id}</span>} />
        <Field label="Item ID" value={<span className="font-mono text-xs">{passport.item_id}</span>} />
        <Field label="RFID" value={passport.rfid_tag ? <span className="font-mono">{passport.rfid_tag}</span> : "—"} />
        <Field label={t("passport.issued")} value={formatDateTime(passport.created_at)} />
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading">{t("passport.testResults")}</h2>
        <DictTable data={passport.test_data} />
      </section>

      {passport.performance_metrics && Object.keys(passport.performance_metrics).length > 0 && (
        <section className="card p-5">
          <h2 className="text-base text-text-heading">{t("passport.metrics")}</h2>
          <DictTable data={passport.performance_metrics} />
        </section>
      )}
    </>
  );
}

function DictTable({ data }: { data: Record<string, unknown> }) {
  const { t } = useTranslation();
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <p className="text-text-muted text-sm mt-3">{t("common.empty")}</p>;
  }
  return (
    <table className="w-full text-sm mt-4">
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k} className="border-t border-border first:border-0">
            <td className="px-2 py-2 font-mono text-text-muted w-1/3">{k}</td>
            <td className="px-2 py-2 font-mono">
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IssueForm({ itemId, rfidTag }: { itemId: string; rfidTag: string | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [json, setJson] = useState<string>(
    JSON.stringify({ salt_rejection: 0.995, permeate_flow_lph: 1100, pressure_bar: 15.5 }, null, 2),
  );
  const [perfJson, setPerfJson] = useState<string>("");
  const [tag, setTag] = useState(rfidTag ?? "");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (data: ProductPassportCreate) => passportsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passport"] }),
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    let testData: Record<string, unknown>;
    let perf: Record<string, unknown> | null = null;
    try {
      testData = JSON.parse(json);
    } catch {
      setErr(t("passport.errTestDataJson"));
      return;
    }
    if (perfJson.trim()) {
      try {
        perf = JSON.parse(perfJson);
      } catch {
        setErr(t("passport.errPerfJson"));
        return;
      }
    }
    mut.mutate({
      item_id: itemId,
      test_data: testData,
      performance_metrics: perf,
      rfid_tag: tag || null,
    });
  }

  return (
    <section className="card p-5 space-y-4">
      <h2 className="text-base text-text-heading">{t("passport.notIssued")}</h2>
      <p className="text-text-muted text-sm">
        {t("passport.issueHint")}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("passport.rfidTag")}</span>
          <input
            type="text"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">test_data (JSON)</span>
          <textarea
            value={json}
            onChange={(e) => setJson(e.target.value)}
            rows={6}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("passport.perfJsonLabel")}</span>
          <textarea
            value={perfJson}
            onChange={(e) => setPerfJson(e.target.value)}
            rows={4}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
            placeholder='{"avg_pressure_bar": 14.8, "deviation_percent": 1.2}'
          />
        </label>
        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
        <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
          {mut.isPending ? t("passport.issuing") : t("passport.issue")}
        </button>
      </form>
    </section>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider font-mono text-text-muted">{label}</p>
      <p className="mt-1">{value}</p>
    </div>
  );
}
